import React, { useState, useEffect } from 'react';
import { MapPin, MessageCircle, Instagram, Clock, Mail, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { fetchStoreInfo } from '../services/api';

const ContactPage = () => {
  const { language, t } = useLanguage();
  const [storeInfo, setStoreInfo] = useState({});

  useEffect(() => {
    fetchStoreInfo()
      .then((data) => setStoreInfo(data))
      .catch((err) => console.error('Failed to load store info:', err));
  }, []);

  const handleWhatsApp = () => {
    if (storeInfo.whatsapp) {
      window.open(`https://wa.me/${storeInfo.whatsapp.replace(/[^0-9]/g, '')}`, '_blank');
    }
  };

  const addresses = Array.isArray(storeInfo.addresses) && storeInfo.addresses.length > 0
    ? storeInfo.addresses
    : [{
        id: 'default',
        label_tr: 'Mağaza',
        label_en: 'Store',
        address_tr: storeInfo.address || '',
        address_en: storeInfo.addressEn || storeInfo.address || '',
        maps_url: '',
      }];

  // Convert any Google Maps URL into an embeddable form so the iframe just works.
  const toEmbedUrl = (url) => {
    if (!url) return null;
    if (url.includes('/embed')) return url;
    // Extract @lat,lng,zoom from typical share URLs
    const at = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)(?:,(\d+(?:\.\d+)?)z)?/);
    if (at) {
      const [, lat, lng, zoom = '15'] = at;
      return `https://www.google.com/maps?q=${lat},${lng}&z=${Math.round(parseFloat(zoom))}&output=embed`;
    }
    // Fall back: extract the q= or place name from the URL
    try {
      const u = new URL(url);
      const q = u.searchParams.get('q') || u.pathname.split('/place/')[1]?.split('/')[0];
      if (q) return `https://www.google.com/maps?q=${encodeURIComponent(decodeURIComponent(q))}&output=embed`;
    } catch (_) { /* ignore */ }
    return null;
  };

  // Pick the first address that produces an embeddable map URL
  const firstMaps = addresses
    .map((a) => ({ ...a, _embed: toEmbedUrl(a.maps_url) }))
    .find((a) => a._embed);

  return (
    <div className="min-h-screen bg-[#fffef2]">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 bg-[#f6f5e8]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-4xl md:text-5xl font-light text-[#333333] mb-4 tracking-wide">
              {t('contact.title')}
            </h1>
            <p className="text-[#666666] text-base font-light max-w-xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-24 lg:py-32 bg-[#fffef2]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
              {/* Left - Contact Details */}
              <div>
                <h2 className="text-2xl md:text-3xl font-light text-[#333333] mb-12 tracking-wide">
                  {language === 'tr' ? 'İletişim Bilgileri' : 'Contact Information'}
                </h2>

                <div className="space-y-8">
                  {/* Addresses (multi) */}
                  {addresses.map((addr, idx) => {
                    const label = language === 'tr' ? addr.label_tr : addr.label_en;
                    const text = language === 'tr' ? addr.address_tr : addr.address_en;
                    return (
                      <div key={addr.id || idx} className="flex items-start gap-6" data-testid={`contact-address-${idx}`}>
                        <div className="w-14 h-14 bg-[#f6f5e8] flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-[#b8860b]" />
                        </div>
                        <div>
                          <h3 className="text-[#333333] text-lg font-medium mb-2">
                            {label || t('contact.address')}
                          </h3>
                          <p className="text-[#666666] text-base leading-relaxed">{text}</p>
                          {addr.maps_url && (
                            <a
                              href={addr.maps_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`contact-address-${idx}-maps-link`}
                              className="inline-flex items-center gap-1.5 text-[#b8860b] text-sm hover:text-[#8a6608] mt-2 transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              {language === 'tr' ? 'Haritada Göster' : 'View on Maps'}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Email */}
                  {storeInfo.email && (
                    <div className="flex items-start gap-6" data-testid="contact-email-block">
                      <div className="w-14 h-14 bg-[#f6f5e8] flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-[#b8860b]" />
                      </div>
                      <div>
                        <h3 className="text-[#333333] text-lg font-medium mb-2">
                          {language === 'tr' ? 'E-posta' : 'Email'}
                        </h3>
                        <a
                          href={`mailto:${storeInfo.email}`}
                          className="text-[#666666] text-base hover:text-[#b8860b] transition-colors"
                          data-testid="contact-email-link"
                        >
                          {storeInfo.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* WhatsApp */}
                  <div className="flex items-start gap-6" data-testid="contact-whatsapp-block">
                    <div className="w-14 h-14 bg-[#f6f5e8] flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-6 h-6 text-[#b8860b]" />
                    </div>
                    <div>
                      <h3 className="text-[#333333] text-lg font-medium mb-2">WhatsApp</h3>
                      <p className="text-[#666666] text-base">
                        {storeInfo.whatsappDisplay || storeInfo.whatsapp}
                      </p>
                    </div>
                  </div>

                  {/* Instagram */}
                  {storeInfo.instagram && (
                    <div className="flex items-start gap-6" data-testid="contact-instagram-block">
                      <div className="w-14 h-14 bg-[#f6f5e8] flex items-center justify-center flex-shrink-0">
                        <Instagram className="w-6 h-6 text-[#b8860b]" />
                      </div>
                      <div>
                        <h3 className="text-[#333333] text-lg font-medium mb-2">Instagram</h3>
                        <a
                          href={`https://instagram.com/${storeInfo.instagram}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#666666] text-base hover:text-[#e1306c] transition-colors"
                        >
                          @{storeInfo.instagram}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Hours */}
                  <div className="flex items-start gap-6" data-testid="contact-hours-block">
                    <div className="w-14 h-14 bg-[#f6f5e8] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#b8860b]" />
                    </div>
                    <div>
                      <h3 className="text-[#333333] text-lg font-medium mb-2">
                        {language === 'tr' ? 'Çalışma Saatleri' : 'Working Hours'}
                      </h3>
                      <p className="text-[#666666] text-base whitespace-pre-line">
                        {language === 'tr'
                          ? storeInfo.hours_tr || 'Pazartesi - Cumartesi: 09:00 - 19:00'
                          : storeInfo.hours_en || 'Monday - Saturday: 09:00 - 19:00'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Button */}
                <button
                  onClick={handleWhatsApp}
                  data-testid="contact-whatsapp-cta"
                  className="mt-12 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-5 bg-[#25d366] text-white text-sm font-medium tracking-widest uppercase transition-all duration-300 hover:bg-[#128c7e]"
                >
                  <MessageCircle className="w-5 h-5" />
                  {t('contact.whatsapp')}
                </button>
              </div>

              {/* Right - Map */}
              <div className="aspect-square lg:aspect-auto bg-[#f6f5e8] overflow-hidden">
                {firstMaps ? (
                  <iframe
                    src={firstMaps._embed}
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: '400px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Altınzade Location"
                  />
                ) : (
                  <div className="w-full h-full min-h-[400px] flex items-center justify-center p-10 text-center">
                    <div>
                      <MapPin className="w-10 h-10 text-[#b8860b] mx-auto mb-4 opacity-60" />
                      <p className="text-[#666] text-sm leading-relaxed">
                        {language === 'tr'
                          ? 'Adreslerimiz için yukarıdaki "Haritada Göster" bağlantılarını kullanabilirsiniz.'
                          : 'Use the "View on Maps" links above to navigate to any of our addresses.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
